import { Link } from 'react-router-dom';
import LegalLayout, { Section, InfoBox, DataTable } from './LegalLayout';

export default function PrivacyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle="How Gromly collects, uses, and protects your personal data."
      lastUpdated="Last updated: October 4, 2026 · Effective date: October 4, 2026"
      relatedLink={{ to: '/terms', label: 'Terms of Service →' }}
    >
      <Section number="0" title="Overview">
        <p>
          This Privacy Policy explains how Gromly ("we", "us") collects, uses, and protects your personal
          data when you use our collaborative trip planning service. We comply with the EU General Data
          Protection Regulation (GDPR) and applicable Spanish data protection law.
        </p>
      </Section>

      <Section number="1" title="Data Controller">
        <p>The data controller responsible for your personal data is:</p>
        <InfoBox>
          <p><strong>VESTIGI PLATFORM, S.L.</strong> (NIF B26661439)</p>
          <p>Passatge Beat F X Ponsa, 11, 08180 Moià (Barcelona), Spain (European Union)</p>
          <p className="text-slate-500 dark:text-slate-400">
            Email:{' '}
            <a href="mailto:contact@gromly.io" className="text-teal-600 dark:text-teal-400 hover:underline">
              contact@gromly.io
            </a>
          </p>
        </InfoBox>
        <p>
          For any questions about how we handle your data, to exercise your rights, or to report a concern,
          please contact us at the email above.
        </p>
      </Section>

      <Section number="2" title="Data We Collect and Why">
        <p>We collect the following categories of personal data:</p>
        <DataTable
          headers={['Category', 'Examples', 'Purpose']}
          rows={[
            ['Account data', 'Email, username, display name, avatar', 'Create and manage your account'],
            ['Authentication data', 'Password hash, Google ID, 2FA secret, session tokens', 'Authenticate you securely'],
            ['Trip content', 'Destinations, dates, itineraries, activities, venues, notes', 'Provide the trip planning service'],
            ['Financial data', 'Expense amounts, currencies, payment methods, debt balances', 'Expense splitting and tracking'],
            ['Location data', 'GPS coordinates and addresses for activities and venues', 'Map display and place search'],
            ['Uploaded documents', 'Booking PDFs and images (up to 10 MB each)', 'Store travel booking confirmations'],
            ['Usage credits', 'Trip credits, AI credits, premium credits', 'Manage service entitlements'],
            ['Social data', 'Followers, following relationships', 'Community features'],
            ['Technical data', 'Error logs, browser type (via Sentry)', 'Detect and fix technical issues'],
          ]}
        />
      </Section>

      <Section number="3" title="Legal Basis for Processing (GDPR Article 6)">
        <ul className="list-disc list-inside space-y-2">
          <li><strong>Contract performance (Art. 6(1)(b)):</strong> Account data, trip content, financial data, uploaded documents — necessary to deliver the service you signed up for.</li>
          <li><strong>Legitimate interests (Art. 6(1)(f)):</strong> Error monitoring and security logs — necessary to maintain a stable and secure service.</li>
          <li><strong>Legal obligation (Art. 6(1)(c)):</strong> Payment records — retained as required by applicable tax and accounting law.</li>
          <li><strong>Consent (Art. 6(1)(a)):</strong> Any optional features explicitly described as consent-based (e.g., future analytics or marketing).</li>
        </ul>
      </Section>

      <Section number="4" title="Third-Party Service Providers">
        <p>
          We share data with the following sub-processors to operate the service. All are bound by data
          processing agreements and, where applicable, EU Standard Contractual Clauses (SCCs) for transfers outside the EU:
        </p>
        <DataTable
          headers={['Provider', 'Purpose', 'Data sent', 'Location']}
          rows={[
            ['Sentry', 'Error monitoring', 'User ID, username, error stack traces', 'USA (SCCs)'],
            ['Stripe', 'Payment processing', 'Email, payment card data (handled directly by Stripe)', 'USA / EU (SCCs)'],
            ['Google (OAuth + Maps)', 'Authentication, place search and maps', 'OAuth profile data; place search queries', 'USA / EU (SCCs)'],
            ['OpenAI', 'AI-powered trip planning assistance', 'Trip content included in AI prompts; trip photos you ask it to place', 'USA (SCCs)'],
            ['Cloudflare R2', 'File storage for booking documents', 'Uploaded files (PDFs, images)', 'EU'],
          ]}
        />
        <p className="text-xs text-slate-500 dark:text-slate-400">
          We do not sell your data to third parties or use it for advertising purposes.
        </p>
      </Section>

      <Section number="5" title="Cookies and Local Storage">
        <p>Gromly uses <strong>one cookie</strong>:</p>
        <InfoBox>
          <p><code className="font-bold">refreshToken</code> — HTTP-only, Secure, SameSite</p>
          <p className="text-slate-500 dark:text-slate-400">Purpose: keeps you logged in. Expires after 7 days or on logout.</p>
          <p className="text-slate-500 dark:text-slate-400">Category: <strong>Strictly necessary</strong> — no consent required under ePrivacy Directive.</p>
        </InfoBox>
        <p>
          We do not use tracking cookies, advertising cookies, or analytics cookies.
          Your authentication access token is stored in memory only and is never written to disk.
        </p>
      </Section>

      <Section number="6" title="Data Retention">
        <ul className="list-disc list-inside space-y-2">
          <li><strong>Account and trip data:</strong> Retained for as long as your account is active. When you delete your account, all your data is permanently deleted within 30 days.</li>
          <li><strong>Payment records:</strong> Retained for 5 years as required by Spanish tax law (Ley General Tributaria).</li>
          <li><strong>Session tokens:</strong> Refresh tokens expire after 7 days. Revoked immediately on logout or account deletion.</li>
          <li><strong>Error logs (Sentry):</strong> Retained for 90 days.</li>
        </ul>
      </Section>

      <Section number="7" title="Your Rights (GDPR)">
        <p>
          As a data subject under GDPR, you have the following rights. To exercise any of them, email us at{' '}
          <a href="mailto:contact@gromly.io" className="text-teal-600 dark:text-teal-400 hover:underline">
            contact@gromly.io
          </a>
          . We will respond within 30 days.
        </p>
        <ul className="list-disc list-inside space-y-2">
          <li><strong>Right of access (Art. 15):</strong> Request a copy of all personal data we hold about you. You can also use "Export my data" in account settings.</li>
          <li><strong>Right to rectification (Art. 16):</strong> Correct inaccurate data via your account profile.</li>
          <li><strong>Right to erasure (Art. 17):</strong> Delete your account and all associated data from account settings.</li>
          <li><strong>Right to data portability (Art. 20):</strong> Export your data in machine-readable JSON format via account settings.</li>
          <li><strong>Right to object (Art. 21):</strong> Object to processing based on legitimate interests.</li>
          <li><strong>Right to restriction of processing (Art. 18):</strong> Request that we restrict how we use your data while a dispute is pending.</li>
        </ul>
        <p>
          You also have the right to lodge a complaint with the Spanish data protection authority:{' '}
          <strong>Agencia Española de Protección de Datos (AEPD)</strong> at{' '}
          <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-teal-600 dark:text-teal-400 hover:underline">
            www.aepd.es
          </a>.
        </p>
      </Section>

      <Section number="8" title="International Data Transfers">
        <p>
          Some of our service providers (Sentry, Stripe, Google, OpenAI) are based in the United States.
          These transfers are covered by Standard Contractual Clauses (SCCs) approved by the European
          Commission, ensuring your data receives a level of protection equivalent to GDPR standards.
        </p>
        <p>
          OpenAI processes trip content you submit to the AI assistant, and a reduced copy of the
          photos you ask it to place on a stop. This data is used to generate responses and is not
          used to train OpenAI models (under our API usage agreement).
        </p>
      </Section>

      <Section number="9" title="Data Security">
        <p>We implement appropriate technical and organisational measures to protect your data:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Passwords are hashed using bcrypt and never stored in plain text.</li>
          <li>Session tokens are signed JWTs; refresh tokens are stored as hashes.</li>
          <li>All communications use HTTPS/TLS in transit.</li>
          <li>Authentication cookies are HttpOnly and Secure.</li>
          <li>Two-factor authentication (TOTP) is available and encouraged.</li>
        </ul>
      </Section>

      <Section number="10" title="Children's Privacy">
        <p>
          Gromly is not directed at children under 16. We do not knowingly collect personal data from
          anyone under 16. If you believe a child has provided us with personal data, please contact us
          and we will delete it promptly.
        </p>
      </Section>

      <Section number="11" title="Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. We will notify registered users of
          material changes by email or via an in-app notice. The "Last updated" date at the top of
          this page reflects the most recent revision.
        </p>
      </Section>
    </LegalLayout>
  );
}
